'use client';

import {
  Children,
  type ComponentType,
  Fragment,
  type ReactElement,
  type ReactNode,
  cloneElement,
  forwardRef,
  isValidElement,
  useId,
  useMemo,
} from 'react';

import { cx } from '@/styled-system/css';

import { useControllableValue } from '../../hooks/use-controllable-value';
import { Slot } from '../slot';
import { ShowHideRootContext } from './context/show-hide-root.context';
import { ShowHideButton } from './show-hide-button';
import { showHideButtonRecipe } from './styles/show-hide-button.styles';
import type { ShowHideRootPropsInterface } from './types/show-hide-root.types';

/**
 * @since 10.0.0
 */

export const ShowHideRoot = forwardRef<HTMLDivElement, ShowHideRootPropsInterface>(
  (props: ShowHideRootPropsInterface, forwardedRef) => {
    const {
      appearance = 'block',
      children,
      defaultHidden = true,
      isHidden,
      onDisplayChange,
      renderButton,
      asChild,
      ...rest
    } = props;

    const id = useId();

    const [isHiddenState, setIsHiddenState] = useControllableValue<boolean | undefined>({
      controlledValue: isHidden,
      uncontrolledValue: defaultHidden,
      isControlled: 'isHidden' in props,
      isUncontrolled: 'defaultHidden' in props,
      onChange: () => onDisplayChange?.(),
      componentName: 'ShowHide.Root',
      controlledPropName: 'isHidden',
      uncontrolledPropName: 'defaultHidden',
    });

    const button = useMemo(() => {
      if (typeof renderButton === 'function') {
        const customButton = renderButton({
          ...props,
          'aria-controls': id,
        });

        /*
         * Apply appearance-based styling to custom button via cloneElement.
         * Assumes the custom button accepts a css prop.
         */
        if (isValidElement(customButton)) {
          return cloneElement(customButton, { css: showHideButtonRecipe.raw({ appearance }) });
        }

        return customButton;
      }

      /* ShowHideButton handles its own margin styling based on appearance context */
      return (
        <ShowHideButton aria-controls={id}>
          {isHiddenState ? 'See more' : 'See less'}
        </ShowHideButton>
      );
    }, [renderButton, props, id, isHiddenState, appearance]);

    const modifiedChildren = useMemo(() => {
      const singleChild = Children.only(children) as ReactElement;

      let content: ReactNode = Children.toArray(singleChild.props.children).map((child, index) => {
        if (
          isValidElement(child) &&
          (child.type as ComponentType<unknown>).displayName === 'ShowHide.Hidden'
        ) {
          return cloneElement(child, {
            ...child.props,
            isHidden: isHiddenState,
            key: index,
          });
        }
        return child;
      });

      if (appearance === 'inline') {
        content = (
          <Fragment>
            {content}
            {isHiddenState ? ` ...` : null}
            {` `}
            {button}
          </Fragment>
        );
      }

      return isValidElement(children)
        ? cloneElement(children as ReactElement, {
            ...(children as ReactElement).props,
            children: content,
          })
        : null;
    }, [children, isHiddenState, appearance, button]);

    const Component = asChild ? Slot : 'div';

    const contextValue = useMemo(
      () => ({ appearance, isHidden: isHiddenState, setIsHidden: setIsHiddenState }),
      [appearance, isHiddenState, setIsHiddenState],
    );

    return (
      <ShowHideRootContext.Provider value={contextValue}>
        <Component
          ref={forwardedRef}
          data-c11n-component="ShowHide.Root"
          data-c11n-version={__C11N_VERSION__}
          id={id}
          {...rest}
          className={cx('c11n_show-hide__root', rest.className)}
        >
          <Fragment>
            {modifiedChildren}
            {appearance === 'block' ? button : null}
          </Fragment>
        </Component>
      </ShowHideRootContext.Provider>
    );
  },
);

ShowHideRoot.displayName = 'ShowHide.Root';
